import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { SCREENING_INPUTS } from "@/data/instruments";

const RISK_ORDER = [
  "minimal", "no problem", "sub-threshold", "low-risk", "low", "mild",
  "hazardous", "moderate", "clinical concern",
  "high-risk", "moderately severe", "substantial",
  "severe", "possible dependence", "probable PTSD",
];

function overallSeverity(bands) {
  let best = 0;
  bands.forEach((b) => {
    const idx = RISK_ORDER.indexOf(b);
    if (idx > best) best = idx;
  });
  if (best >= RISK_ORDER.indexOf("severe")) {
    return { band: "High acuity", detail: "Recommend Residential (3.1–3.7) with rapid stabilization; safety planning and MAT evaluation as indicated." };
  }
  if (best >= RISK_ORDER.indexOf("moderate")) {
    return { band: "Moderate acuity", detail: "Recommend IOP or PHP (2.1–2.5) with weekly MBC and warm-handoff aftercare planning." };
  }
  if (best >= RISK_ORDER.indexOf("mild")) {
    return { band: "Mild acuity", detail: "Outpatient (1.0–1.7) with weekly MBC; watch for change." };
  }
  return { band: "Subclinical / minimal", detail: "Screening negative for immediate clinical concern; document and re-screen per policy." };
}

export default function ScreeningBattery() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const bands = SCREENING_INPUTS.map((inp) => ({
    id: inp.id,
    label: inp.label,
    value: values[inp.id],
    band: values[inp.id] === undefined || values[inp.id] === "" ? null : inp.band(Number(values[inp.id])),
  }));
  const complete = bands.every((b) => b.band !== null);
  const overall = complete ? overallSeverity(bands.map((b) => b.band)) : null;

  const save = async () => {
    if (!complete) {
      toast.error("Enter every screening score.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "screening_battery",
        patient_identifier: patient || null,
        responses: {
          values,
          bands: Object.fromEntries(bands.map((b) => [b.id, b.band])),
        },
        score: Number(values.phq9 || 0) + Number(values.gad7 || 0),
        interpretation: overall.band,
        notes: notes || null,
      });
      toast.success("Screening battery saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="screening-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="screening-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Screening Battery Scorer"
        subtitle="Enter totals from an intake screening battery. Output shows per-instrument bands and an overall acuity recommendation." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="screening-patient" />
          </div>

          <div className="card p-5 space-y-4">
            {SCREENING_INPUTS.map((inp) => (
              <div key={inp.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3" data-testid={`screening-input-${inp.id}`}>
                <label className="text-sm text-navy font-medium">{inp.label}</label>
                <input
                  type="number"
                  min={0}
                  max={inp.max}
                  value={values[inp.id] ?? ""}
                  onChange={(e) => setValues({ ...values, [inp.id]: e.target.value })}
                  className="field w-24 text-right"
                  placeholder={`0–${inp.max}`}
                  data-testid={`screening-${inp.id}`}
                />
                {values[inp.id] !== undefined && values[inp.id] !== "" && (
                  <span className="badge badge-neutral capitalize">{inp.band(Number(values[inp.id]))}</span>
                )}
              </div>
            ))}
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="screening-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="screening-summary">
            <div className="overline mb-2">Overall acuity</div>
            {overall ? (
              <>
                <div className="text-2xl font-semibold text-navy leading-tight fade-up">{overall.band}</div>
                <p className="text-sm text-ink-muted mt-3 leading-relaxed">{overall.detail}</p>
              </>
            ) : (
              <div className="text-sm text-ink-muted">Complete every instrument to see the acuity recommendation.</div>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={!complete || saving} className="btn btn-primary w-full justify-center" data-testid="screening-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
